/**
 * Customer Service
 * API calls for customer management
 */

import api from './api.js';
import type { ApiResponse, PaginatedResponse } from '@/types/api.js';
import type {
  Customer,
  CustomerCreateInput,
  CustomerUpdateInput,
  CustomerListParams,
  CustomerStatistics,
  CustomerContact,
} from '@/types/customer.js';

export const customersService = {
  /**
   * Get paginated list of customers
   */
  async list(params: CustomerListParams = {}): Promise<PaginatedResponse<Customer>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Customer>>>('/customers', {
      params,
    });
    return response.data.data;
  },

  /**
   * Get customer by ID
   */
  async getById(id: string): Promise<Customer> {
    const response = await api.get<ApiResponse<Customer>>(`/customers/${id}`);
    return response.data.data;
  },

  /**
   * Create new customer
   */
  async create(data: CustomerCreateInput): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>('/customers', data);
    return response.data.data;
  },

  /**
   * Update customer
   */
  async update(id: string, data: CustomerUpdateInput): Promise<Customer> {
    const response = await api.put<ApiResponse<Customer>>(`/customers/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete customer
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/customers/${id}`);
  },

  /**
   * Update customer status
   */
  async updateStatus(id: string, status: 'active' | 'inactive'): Promise<Customer> {
    const response = await api.patch<ApiResponse<Customer>>(`/customers/${id}/status`, {
      status,
    });
    return response.data.data;
  },

  /**
   * Bulk delete customers
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await api.post('/customers/bulk-delete', { ids });
  },

  /**
   * Get customer statistics
   */
  async getStatistics(): Promise<CustomerStatistics> {
    const response = await api.get<ApiResponse<CustomerStatistics>>('/customers/statistics');
    return response.data.data;
  },

  // Customer Contacts

  /**
   * Get customer contacts
   */
  async getContacts(customerId: string): Promise<CustomerContact[]> {
    const response = await api.get<ApiResponse<CustomerContact[]>>(
      `/customers/${customerId}/contacts`
    );
    return response.data.data;
  },

  /**
   * Create customer contact
   */
  async createContact(
    customerId: string,
    data: Omit<CustomerContact, 'id' | 'customerId' | 'createdAt' | 'updatedAt'>
  ): Promise<CustomerContact> {
    const response = await api.post<ApiResponse<CustomerContact>>(
      `/customers/${customerId}/contacts`,
      data
    );
    return response.data.data;
  },

  /**
   * Update customer contact
   */
  async updateContact(
    customerId: string,
    contactId: string,
    data: Partial<CustomerContact>
  ): Promise<CustomerContact> {
    const response = await api.put<ApiResponse<CustomerContact>>(
      `/customers/${customerId}/contacts/${contactId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete customer contact
   */
  async deleteContact(customerId: string, contactId: string): Promise<void> {
    await api.delete(`/customers/${customerId}/contacts/${contactId}`);
  },
};

export default customersService;
